import { ChangeEvent, ReactNode } from "react"

import { Trans } from "react-i18next"

import { FileUpload } from "components/inputs/FileUpload"
import { MoneyAmountInput } from "components/inputs/MoneyAmountInput"
import { MultiSelect } from "components/inputs/MultiSelect"
import { TagsMultiSelect } from "components/inputs/TagsMultiSelect"
import { TitledCheckbox } from "components/inputs/TitledCheckbox"
import { VerificationCheckboxes } from "components/inputs/VerificationCheckboxes"

import { countries } from "constants/countries"
import { FALLBACK_CURRENCIES } from "constants/currencies"

import { AdminBonus2 } from "services/bonus/type"

import {
  ALLOWED_PRODUCT_OPTIONS,
  BONUS_LOCALE_OPTIONS,
  BONUS_MODE_OPTIONS,
  bonusLocaleLabel,
  CALCULATION_MODE_OPTIONS,
  ELIGIBLE_PAYMENT_OPTIONS,
  FIELD_TOOLTIPS,
  intOrClear,
  isNoDepositType,
  numOrClear,
  rangeWarning,
  SELECTABLE_ACTIVATION_EVENT_OPTIONS,
  SELECTABLE_BONUS_TYPE_OPTIONS,
  spinsGamesOutsideAllowList,
  STACKING_MODE_OPTIONS,
  WAGERING_BASE_OPTIONS,
  WAGERING_CONTRIBUTION_OPTIONS,
  WEEKDAY_OPTIONS
} from "../../helpers/bonus2.helpers"
import { issuesForCurrency, stakeIssueMessage, stakeIssueTarget } from "../../helpers/spin-stake.helpers"
import { PromoCodeListEditor } from "../promo-codes/PromoCodeListEditor"
import { TagMultiSelect } from "../shared/TagMultiSelect"

import {
  CollapsibleGamePicker,
  controlLineClassName,
  DescribeStrip,
  Field,
  inputClassName,
  labelClassName,
  selectClassName,
  TimezoneSelect,
  Tooltip
} from "./bonus-form-ui"
import { BetValuesSyncCard, SpinStakeCell } from "./SpinStakeFields"
import { BonusFormCtx } from "./useBonusForm"

// ---------------------------------------------------------------------------
// Accordion section ids (one section is expanded at a time). Grouping mirrors the
// real AdminBonus2 shape — NOT the manager's AI mock; every field below maps to a
// real backend field via handleChange.
// ---------------------------------------------------------------------------
export type SectionId = "basic" | "amount" | "wagering" | "scope" | "violations" | "activation" | "content" | "advanced"

// -----------------------------------------------------------------------
// Section metadata (number, title, summary). Rendered as accordion panels.
// `title`/`summary` are translation KEYS — resolved at the render site, so this
// stays a static table rather than becoming a hook.
// -----------------------------------------------------------------------
export const sectionMeta: Array<{ id: SectionId; title: string; summary: string }> = [
  { id: "basic", title: "bonus2.sections.basic.title", summary: "bonus2.sections.basic.summary" },
  { id: "amount", title: "bonus2.sections.amount.title", summary: "bonus2.sections.amount.summary" },
  { id: "wagering", title: "bonus2.sections.wagering.title", summary: "bonus2.sections.wagering.summary" },
  { id: "activation", title: "bonus2.sections.activation.title", summary: "bonus2.sections.activation.summary" },
  { id: "content", title: "bonus2.sections.content.title", summary: "bonus2.sections.content.summary" },
  { id: "scope", title: "bonus2.sections.scope.title", summary: "bonus2.sections.scope.summary" },
  { id: "advanced", title: "bonus2.sections.advanced.title", summary: "bonus2.sections.advanced.summary" },
  { id: "violations", title: "bonus2.sections.violations.title", summary: "bonus2.sections.violations.summary" }
]

export const renderSectionBody = (id: SectionId, ctx: BonusFormCtx): ReactNode => {
  const {
    formData,
    handleChange,
    handleTypeChange,
    handleModeChange,
    handleStackingChange,
    handleWelcomeBonusToggle,
    isWelcomeBonus,
    handleCashoutModeChange,
    maxCashoutMode,
    toggleAction,
    toggleGameGroup,
    handleCreateBanner,
    gameGroups,
    cmsBanners,
    selectCmsBanner,
    bannerMode,
    setBannerMode,
    newBannerName,
    setNewBannerName,
    newBannerDesktopUrl,
    setNewBannerDesktopUrl,
    newBannerMobileUrl,
    setNewBannerMobileUrl,
    savingBanner,
    selectedBanner,
    showFreeSpins,
    showEligibilityScope,
    isFreeSpinsOnly,
    isNoDeposit,
    showPercentageAmount,
    isPercentage,
    isAiDynamic,
    showPromoCode,
    preventLaunchSelected,
    betValues,
    saveIssues,
    cashoutPreview,
    cashoutPreviewIsUncapped,
    freeSpinsWageringPreview,
    freeSpinsWageringHasNoWagering,
    wageringPreview,
    contributionDescribe,
    modeDescribe,
    enabledCurrencies,
    tenant,
    refusedCodes,
    savedPromoCodes,
    t
  } = ctx

  // Iteration-3 Fix 12 — toggle a day/hour in the recurring-availability window and
  // set the timezone. Uses the generic handleChange to write the nested object.
  const handleTimeRestrictionToggle = (axis: "days" | "hours", value: number) => {
    const current = formData.time_restrictions ?? {}
    const list = current[axis] ?? []
    const next = list.includes(value) ? list.filter((v) => v !== value) : [...list, value].sort((a, b) => a - b)
    handleChange("time_restrictions", { ...current, [axis]: next })
  }
  const handleTimeRestrictionTimezone = (tz: string) => {
    const current = formData.time_restrictions ?? {}
    handleChange("time_restrictions", { ...current, timezone: tz || undefined })
  }

  const timezoneWithoutWindow =
    Boolean(formData.time_restrictions?.timezone) &&
    (formData.time_restrictions?.days?.length ?? 0) === 0 &&
    (formData.time_restrictions?.hours?.length ?? 0) === 0

  // Iteration-3 Fix 04 — per-currency FIXED amounts repeater (localized, no FX). Mirrors the
  // monolith "Add Currency" pattern. Empty = FX-only (the base amount above still applies).
  // Currency options come from the tenant's ENABLED currencies (admin settings `rcoin`); the
  // fallback keeps the picker usable if settings failed to load. Amounts are that currency's
  // MINOR units (cents) — the same units the backend stores and activation reads verbatim.
  // No-Deposit bonuses have no deposit requirement — Min. Deposit is removed completely from
  // the amounts-by-currency table (the grid column collapses), not just hidden as a blank cell.
  // SOFT-736 — a bonus that carries spins adds one more per-currency cell (the spin stake), so
  // the row grid grows a column. Written out in full rather than composed: Tailwind scans the
  // source for literal class names, and an interpolated one is simply never generated.
  const amountRowGridColsClass = (() => {
    if (isNoDeposit) return showFreeSpins ? "md:grid-cols-[7rem_1fr_1fr_auto]" : "md:grid-cols-[7rem_1fr_auto]"
    return showFreeSpins ? "md:grid-cols-[7rem_1fr_1fr_1fr_auto]" : "md:grid-cols-[7rem_1fr_1fr_auto]"
  })()
  const currencies = formData.currencies ?? []
  // USD is the REQUIRED base row of the currency table (rendered from the base amount fields and
  // used as the FX source for any coin without its own row), so the additive rows below are the
  // NON-USD localized currencies only — USD can never be added as an extra row.
  const currencyOptions = (enabledCurrencies.length > 0 ? enabledCurrencies : FALLBACK_CURRENCIES).filter((c) => c.toUpperCase() !== "USD")
  const unallowedSpinsGames = spinsGamesOutsideAllowList(formData)
  // Eligibility keeps USD — it is excluded above only because the base fields already are its row.
  const eligibilityCurrencies = enabledCurrencies.length > 0 ? enabledCurrencies : FALLBACK_CURRENCIES
  // The base amount fields ARE the USD row. They live in DOLLARS in form state (converted to cents
  // at submit by bonusDollarsToCents), but MoneyAmountInput works in CENTS — bridge the two at this
  // boundary only, leaving the base save pipeline untouched.
  const dollarsToCents = (d?: number) => (d === null || d === undefined ? undefined : Math.round(d * 100))
  const centsToDollars = (c?: number) => (c === null || c === undefined ? undefined : c / 100)
  // A currency may appear in AT MOST ONE row (one localized amount set per currency). Track the
  // codes already taken so each dropdown hides the others' currencies and "Add currency" stops
  // once every option is used — otherwise the same currency could be added many times.
  const usedCurrencies = new Set(currencies.map((r) => (r.currency || "").toUpperCase()))
  // SOFT-736 — issues from the last refused save that belong to the games field rather than to a
  // currency row (no games named; a game this tenant's catalog does not have).
  const gameIssues = saveIssues.filter((i) => stakeIssueTarget(i.code) === "games")
  const firstFreeCurrency = currencyOptions.find((c) => !usedCurrencies.has(c))
  const addCurrencyRow = () => {
    // Only add when there is a still-unused currency, so a fresh row is immediately valid and can
    // never duplicate an existing one.
    if (!firstFreeCurrency) return
    handleChange("currencies", [...currencies, { currency: firstFreeCurrency }])
  }
  const setRowCurrency = (idx: number, currency: string) => {
    handleChange(
      "currencies",
      currencies.map((row, i) => (i === idx ? { ...row, currency: currency.toUpperCase() } : row))
    )
  }
  // SOFT-736 — the row's spin stake, already in that currency's minor units (the picker converts
  // the provider's major-unit tier). Written into the SAME row object as the cash amounts, so it
  // rides along in `currencies` with no API shape of its own.
  const setRowStake = (idx: number, stakeMinor?: number) => {
    handleChange(
      "currencies",
      currencies.map((row, i) => (i === idx ? { ...row, free_spins_bet_value: stakeMinor } : row))
    )
  }
  const setRowAmount = (idx: number, field: "min_deposit" | "max_bonus_amount" | "amount", amountCents?: number) => {
    handleChange(
      "currencies",
      currencies.map((row, i) => (i === idx ? { ...row, [field]: amountCents } : row))
    )
  }
  const removeCurrencyRow = (idx: number) => {
    handleChange(
      "currencies",
      currencies.filter((_, i) => i !== idx)
    )
  }

  // Iteration-3 Fix 05 — "Add language" for name/description. `en` is the PRIMARY value
  // edited via the main Name/Description field above; additional locales live in the i18n
  // map. Language is chosen from a dropdown (no free-text codes), and each row's locale is
  // FIXED once added — so rows never re-key/reorder while typing (the old free-text version
  // remounted the row on every keystroke, causing focus to jump between languages).
  const i18nField = (field: "name_i18n" | "description_i18n") => (formData[field] ?? {}) as Record<string, string>

  // Locales already in use (including the primary en) — excluded from the "add" dropdown.
  const usedLocales = (field: "name_i18n" | "description_i18n"): string[] => {
    const keys = Object.keys(i18nField(field))
    return keys.includes("en") ? keys : ["en", ...keys]
  }

  const addLocale = (field: "name_i18n" | "description_i18n", primary: string, locale: string) => {
    if (!locale) return
    const map = { ...i18nField(field) }
    if (!map.en && primary) map.en = primary // preserve the primary value in the map
    if (map[locale] === undefined || map[locale] === null) map[locale] = ""
    handleChange(field, map)
  }
  const setLocaleText = (field: "name_i18n" | "description_i18n", locale: string, text: string) => {
    handleChange(field, { ...i18nField(field), [locale]: text })
  }
  const removeLocale = (field: "name_i18n" | "description_i18n", locale: string) => {
    const map = { ...i18nField(field) }
    delete map[locale]
    handleChange(field, map)
  }

  const renderLanguageRows = (field: "name_i18n" | "description_i18n", primary: string, multiline = false) => {
    const map = i18nField(field)
    // Extra locales (everything except the primary en), in a STABLE order for keys.
    const extraLocales = BONUS_LOCALE_OPTIONS.map((l) => l.code).filter((c) => c !== "en" && c in map)
    const available = BONUS_LOCALE_OPTIONS.filter((l) => !usedLocales(field).includes(l.code))

    return (
      <div className="mt-2 space-y-2">
        {extraLocales.map((locale) => (
          // Key is the FIXED locale code — never the editable text — so no remount on typing.
          <div key={locale} className="flex gap-2 items-start">
            <div className="w-24 shrink-0 px-2 py-2 text-sm text-gray-300 bg-[#0C0C1A] border border-gray-700 rounded">
              {t(bonusLocaleLabel(locale))}
            </div>
            {multiline ? (
              <textarea
                value={map[locale] ?? ""}
                onChange={(e) => setLocaleText(field, locale, e.target.value)}
                placeholder={t("bonus2.form.localeTranslation", { locale: t(bonusLocaleLabel(locale)) })}
                className={`${inputClassName} flex-1 min-h-[60px] py-2`}
              />
            ) : (
              <input
                type="text"
                value={map[locale] ?? ""}
                onChange={(e) => setLocaleText(field, locale, e.target.value)}
                placeholder={t("bonus2.form.localeTranslation", { locale: t(bonusLocaleLabel(locale)) })}
                className={`${inputClassName} flex-1`}
              />
            )}
            <button
              type="button"
              onClick={() => removeLocale(field, locale)}
              className="btn btn-xs btn-error shrink-0 self-center"
              title={t("bonus2.form.removeLanguage")}
            >
              Remove
            </button>
          </div>
        ))}

        {available.length > 0 && (
          <div className="flex items-center gap-2">
            <span className="text-xs text-gray-500">+ Add language:</span>
            <select
              value=""
              onChange={(e) => {
                addLocale(field, primary, e.target.value)
                e.target.value = ""
              }}
              className={`${selectClassName} w-48`}
            >
              <option value="">{t("bonus2.form.selectPlaceholder")}</option>
              {available.map((l) => (
                <option key={l.code} value={l.code}>
                  {t(l.label)}
                </option>
              ))}
            </select>
          </div>
        )}
      </div>
    )
  }

  switch (id) {
    // ------------------------------------------------------------------ 1
    case "basic":
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("bonus2.form.bonusName")} field="name" desc={t("bonus2.form.bonusNameDesc")}>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => handleChange("name", e.target.value)}
              className={inputClassName}
              required
            />
            {/* Fix 05 — localized name variants. */}
            {renderLanguageRows("name_i18n", formData.name ?? "")}
          </Field>
          <Field label={t("bonus2.form.type")} field="type" desc={t("bonus2.form.typeDesc")}>
            <select
              value={formData.type}
              onChange={(e) => handleTypeChange(e.target.value as AdminBonus2["type"])}
              className={selectClassName}
            >
              {/* Fix 13: hide Cashback / Reload / Referral / VIP from selectable types. */}
              {SELECTABLE_BONUS_TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </option>
              ))}
            </select>
          </Field>
          {/* Tickets 3 & 8 — Mode + Calculation Mode are locked (STICKY/FIXED) and hidden for
              No-Deposit and pure Free-Spins bonuses; see the force-set effects in useBonusForm.ts.
              Freespins+Deposit has a real deposit/cash component, so it keeps these editable like
              any other deposit-triggered type. */}
          {!isNoDeposit && !isFreeSpinsOnly && (
            <>
              <Field label={t("bonus2.form.mode")} field="mode" desc={modeDescribe}>
                <select
                  value={formData.mode}
                  onChange={(e) => handleModeChange(e.target.value as AdminBonus2["mode"])}
                  className={selectClassName}
                >
                  {BONUS_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} className="bg-[#0C0C1A]">
                      {t(opt.label)}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label={t("bonus2.form.calculationMode")} field="calculation_mode" desc={t("bonus2.form.calculationModeDesc")}>
                <select
                  value={formData.calculation_mode}
                  onChange={(e) => handleChange("calculation_mode", e.target.value)}
                  className={selectClassName}
                >
                  {CALCULATION_MODE_OPTIONS.map((opt) => (
                    <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                      {t(opt.label)}
                    </option>
                  ))}
                </select>
              </Field>
            </>
          )}
          <Field label={t("bonus2.form.description")} field="description" desc={t("bonus2.form.descriptionDesc")} full>
            <textarea
              value={formData.description || ""}
              onChange={(e) => handleChange("description", e.target.value)}
              className={`${inputClassName} min-h-[120px] py-2`}
              placeholder={t("bonus2.form.descriptionPlaceholder")}
            />
            {/* Fix 05 — localized description variants. */}
            {renderLanguageRows("description_i18n", formData.description || "", true)}
          </Field>
        </div>
      )

    // ------------------------------------------------------------------ 2
    case "amount":
      return (
        <div className="space-y-4">
          {isFreeSpinsOnly && <DescribeStrip>{t("bonus2.form.freeSpinsNoCashMatch")}</DescribeStrip>}

          {/* Free Spins has no cash-match amount (see the `amount` field below), but a deposit
              threshold can still gate the spins grant (e.g. "deposit $20, get 50 free spins") —
              so min_deposit stays, as a single USD field rather than the full per-currency table. */}
          {isFreeSpinsOnly && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field
                label={t("bonus2.form.minDepositDollar")}
                field="min_deposit"
                warn={rangeWarning("min_deposit", formData.min_deposit)}
                desc={t("bonus2.form.minDepositFsDesc")}
              >
                <input
                  type="number"
                  value={formData.min_deposit || ""}
                  onChange={(e) => handleChange("min_deposit", numOrClear(e.target.value))}
                  className={inputClassName}
                  step="0.01"
                />
              </Field>
            </div>
          )}
          {/* The percentage is currency-independent (100% is 100% everywhere), so it stays a single
              base field. All MONEY amounts (min deposit, max bonus / fixed amount) live in the
              per-currency table below, whose FIRST row is the required USD base. */}
          {showPercentageAmount && isPercentage && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field
                label={t("bonus2.form.percentage")}
                field="amount"
                warn={rangeWarning("amount", formData.amount)}
                desc={t("bonus2.form.percentageDesc")}
              >
                <input
                  type="number"
                  value={formData.amount ?? ""}
                  onChange={(e) => handleChange("amount", parseFloat(e.target.value))}
                  className={inputClassName}
                  step="0.01"
                  required
                />
              </Field>
            </div>
          )}

          {/* AI_DYNAMIC keeps its own USD range fields: the per-currency contract stores only
              min_deposit / max_bonus_amount / amount (no range), so a dynamic range can't be
              localized. These stay as base USD amounts (FX-converted downstream). */}
          {showPercentageAmount && isAiDynamic && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Field label={t("bonus2.form.minAmount")} field="amount">
                <input
                  type="number"
                  value={formData.amount_min || ""}
                  onChange={(e) => handleChange("amount_min", numOrClear(e.target.value))}
                  className={inputClassName}
                  step="0.01"
                />
              </Field>
              <Field label={t("bonus2.form.maxAmount")} field="amount">
                <input
                  type="number"
                  value={formData.amount_max || ""}
                  onChange={(e) => handleChange("amount_max", numOrClear(e.target.value))}
                  className={inputClassName}
                  step="0.01"
                />
              </Field>
              <Field
                label={t("bonus2.form.minDepositDollar")}
                field="min_deposit"
                warn={rangeWarning("min_deposit", formData.min_deposit)}
                desc={t("bonus2.form.minDepositDesc")}
              >
                <input
                  type="number"
                  value={formData.min_deposit || ""}
                  onChange={(e) => handleChange("min_deposit", numOrClear(e.target.value))}
                  className={inputClassName}
                  step="0.01"
                />
              </Field>
            </div>
          )}

          {/* Max Cashout (Req 2/3) — XOR multiplier vs fixed amount. Switching mode clears
              the other field so the payload can never carry both. */}
          <div className="rounded-lg border border-[rgba(132,0,168,0.25)] p-4 space-y-3">
            <div className="flex items-center gap-4">
              <span className={labelClassName}>{t("bonus2.form.maxCashout")}</span>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="max_cashout_mode"
                  className="radio radio-primary radio-sm"
                  checked={maxCashoutMode === "multiplier"}
                  onChange={() => handleCashoutModeChange("multiplier")}
                />
                <span className={labelClassName}>{t("bonus2.form.multiplierRadio")}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="max_cashout_mode"
                  className="radio radio-primary radio-sm"
                  checked={maxCashoutMode === "amount"}
                  onChange={() => handleCashoutModeChange("amount")}
                />
                <span className={labelClassName}>{t("bonus2.form.fixedAmountRadio")}</span>
              </label>
            </div>

            {maxCashoutMode === "multiplier" ? (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field
                  label={t("bonus2.form.multiplier")}
                  field="max_cashout_multiplier"
                  warn={rangeWarning("max_cashout_multiplier", formData.max_cashout_multiplier)}
                >
                  <input
                    type="number"
                    value={formData.max_cashout_multiplier || ""}
                    onChange={(e) => handleChange("max_cashout_multiplier", numOrClear(e.target.value))}
                    className={inputClassName}
                    step="0.1"
                  />
                </Field>
                {/* Tickets 2 & 7 — Cap Based On is locked to Bonus Amount for No-Deposit and
                    pure Free-Spins bonuses (the force-set effect in useBonusForm.ts keeps the
                    value correct); shown disabled here rather than hidden, matching the
                    AI-Dynamic / USD-row precedent for "visible but not editable" locked fields.
                    Freespins+Deposit gets the real deposit cap options back — it requires
                    standard deposit cap rules, not a cap locked to freespins winnings. */}
                <Field
                  label={t("bonus2.form.capBasedOn")}
                  field="max_cashout_base"
                  desc={
                    (formData.free_spins_count ?? 0) > 0
                      ? (t("bonus2.form.capBasedOnDescSpins") as string)
                      : (t("bonus2.form.capBasedOnDesc") as string)
                  }
                >
                  {isNoDeposit || isFreeSpinsOnly ? (
                    <select value="BONUS_ONLY" disabled className={selectClassName}>
                      <option value="BONUS_ONLY">{t("bonus2.form.bonusAmountOption")}</option>
                    </select>
                  ) : (
                    <select
                      value={formData.max_cashout_base || ""}
                      onChange={(e) => handleChange("max_cashout_base", e.target.value || null)}
                      className={selectClassName}
                    >
                      <option value="">{t("bonus2.form.selectBase")}</option>
                      {WAGERING_BASE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {t(opt.label)}
                        </option>
                      ))}
                    </select>
                  )}
                </Field>
              </div>
            ) : (
              <Field label={t("bonus2.form.fixedAmountCents")} field="max_cashout_amount">
                <input
                  type="number"
                  value={formData.max_cashout_amount ?? ""}
                  onChange={(e) => handleChange("max_cashout_amount", intOrClear(e.target.value))}
                  className={inputClassName}
                />
              </Field>
            )}
            <DescribeStrip tone={cashoutPreviewIsUncapped ? "warn" : "info"}>
              {cashoutPreview}
            </DescribeStrip>
          </div>

          {/* Ticket 6 — Free Spins Settings, relocated here from Advanced Settings. */}
          {showFreeSpins && (
            <div className="rounded-lg border border-[rgba(132,0,168,0.25)] p-4 space-y-4">
              <h4 className="text-md font-semibold dark:text-white">{t("bonus2.form.freeSpinsSettings")}</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Field label={t("bonus2.form.freeSpinsCount")} field="free_spins_count">
                  <input
                    type="number"
                    value={formData.free_spins_count || ""}
                    onChange={(e) => handleChange("free_spins_count", intOrClear(e.target.value))}
                    className={inputClassName}
                  />
                </Field>
              </div>
              <CollapsibleGamePicker
                label={t("bonus2.form.freeSpinGame")}
                selectedGameIds={formData.free_spins_game_ids || []}
                onGameIdsChange={(gameIds) => handleChange("free_spins_game_ids", gameIds)}
                tenant={tenant}
                ladderProducersOnly
                singleSelect
              />
              {/* SOFT-736 — a games-level issue from the last refused save (no games named, or a
                  game that is not in this tenant's catalog) belongs here, on the selection that
                  caused it. */}
              {gameIssues.length > 0 && (
                <div className="space-y-1">
                  {gameIssues.map((issue, i) => (
                    <p key={`${issue.code}-${issue.game_id ?? i}`} className="text-xs text-rose-400">
                      {stakeIssueMessage(issue, t)}
                    </p>
                  ))}
                </div>
              )}
              {/* The two lists are independent — Allowed Games bounds the cash the bonus pays
                  with, the spins play here. But the bet-time gate reads only the allow list, so a
                  spins game left out of it stops the bonus funding the very game its spins are on
                  once the winnings activate. That is the andeswin case behind ticket #12, and it
                  was on a PURE free-spins bonus — so this warning lives here, next to the games it
                  is about, rather than in the eligibility section that type never shows. */}
              {unallowedSpinsGames.length > 0 && (
                <DescribeStrip tone="warn">
                  {t("bonus2.form.spinsGamesNotAllowed", { games: unallowedSpinsGames.join(", ") })}
                </DescribeStrip>
              )}
              <BetValuesSyncCard betValues={betValues} hasGame={(formData.free_spins_game_ids ?? []).length > 0} />

              {/* SOFT-736 — the per-currency SPIN STAKE table.
                  A pure Free-Spins bonus has no cash amounts, so the "Amounts by currency" table
                  below is not rendered for it — but the stake is a per-currency value it very much
                  does have, and it must be a real tier of the game or the player silently never
                  receives the spins. So the table exists here too, with the stake as its only cell.
                  (DEPOSIT_AND_SPINS keeps ONE table: the stake is a column of the amounts table.) */}
              {isFreeSpinsOnly && (
                <div className="rounded-lg border border-[rgba(132,0,168,0.25)] p-4 space-y-3">
                  <div className="flex items-center justify-between">
                    <span className={labelClassName}>{t("bonus2.form.spinStakeByCurrency")}</span>
                    <button
                      type="button"
                      onClick={addCurrencyRow}
                      disabled={!firstFreeCurrency}
                      className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                      title={firstFreeCurrency ? "" : t("bonus2.everyCurrencyHasRow")}
                    >
                      {t("bonus2.addCurrency")}
                    </button>
                  </div>
                  <p className={`${labelClassName} opacity-70 text-xs`}>
                    The stake must be one of the game&apos;s own bet values for that currency. A currency with no row uses the USD fallback,
                    converted at the live rate and snapped to the nearest tier at launch.
                  </p>
                  <div className="space-y-3">
                    {/* USD is the fallback, not an addable row — it is the template's own stake. */}
                    <div className="grid grid-cols-1 md:grid-cols-[7rem_1fr_auto] gap-2 items-start">
                      <div className="form-control">
                        <label className="label py-1">
                          <span className={labelClassName}>{t("bonus2.currency")}</span>
                        </label>
                        <select className={selectClassName} value="USD" disabled aria-label={t("bonus2.currency")}>
                          <option value="USD">USD</option>
                        </select>
                      </div>
                      <SpinStakeCell
                        coin="USD"
                        value={formData.free_spins_bet_value}
                        onChange={(minor) => handleChange("free_spins_bet_value", minor)}
                        betValues={betValues}
                        hasGame={(formData.free_spins_game_ids ?? []).length > 0}
                        issues={issuesForCurrency(saveIssues, "USD")}
                        label={t("bonus2.form.spinStakeFallback")}
                        isFallback
                      />
                      <div />
                    </div>

                    {currencies.map((row, idx) => {
                      const rowCurrencyOptions = currencyOptions.filter((c) => c === row.currency || !usedCurrencies.has(c))
                      return (
                        <div
                          key={row.currency || `stake-row-${idx}`}
                          className="grid grid-cols-1 md:grid-cols-[7rem_1fr_auto] gap-2 items-start"
                        >
                          <div className="form-control">
                            <label className="label py-1">
                              <span className={labelClassName}>{t("bonus2.currency")}</span>
                            </label>
                            <select
                              className={selectClassName}
                              value={row.currency}
                              onChange={(e) => setRowCurrency(idx, e.target.value)}
                              aria-label={t("bonus2.currency")}
                            >
                              {row.currency && !currencyOptions.includes(row.currency) && (
                                <option value={row.currency}>{row.currency}</option>
                              )}
                              {rowCurrencyOptions.map((c) => (
                                <option key={c} value={c}>
                                  {c}
                                </option>
                              ))}
                            </select>
                          </div>
                          <SpinStakeCell
                            coin={row.currency}
                            value={row.free_spins_bet_value}
                            onChange={(minor) => setRowStake(idx, minor)}
                            betValues={betValues}
                            hasGame={(formData.free_spins_game_ids ?? []).length > 0}
                            issues={issuesForCurrency(saveIssues, row.currency)}
                          />
                          <div className="form-control">
                            <label className="label py-1">
                              <span className={labelClassName}>&nbsp;</span>
                            </label>
                            <div className={controlLineClassName}>
                              <button type="button" onClick={() => removeCurrencyRow(idx)} className="btn btn-xs btn-error">
                                {t("bonus2.remove")}
                              </button>
                            </div>
                          </div>
                        </div>
                      )
                    })}
                  </div>
                </div>
              )}
              <Field label={t("bonus2.form.freeSpinsWagering")} desc={t("bonus2.form.freeSpinsWageringDesc")}>
                {/* Whole multipliers only: bonus-service truncates this (Math.trunc), so 0.5 would
                    reach the player as 0 — no wagering at all, the opposite of "half a turnover". */}
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={formData.free_spins_wagering ?? 1}
                  onChange={(e) => handleChange("free_spins_wagering", parseInt(e.target.value) || 0)}
                  className={inputClassName}
                />
              </Field>
              <DescribeStrip tone={freeSpinsWageringHasNoWagering ? "warn" : "info"}>{freeSpinsWageringPreview}</DescribeStrip>
            </div>
          )}

          {/* Iteration-3 Fix 04 — amounts BY CURRENCY. The FIRST row is the required USD base
              (bound to the base amount fields; it is also the FX fallback for any coin without its
              own row). Additional NON-USD rows carry clean localized numbers (e.g. €500 instead of a
              converted €461.23), each in that currency's minor units. The second money cell depends
              on the calculation mode: percentage → Max bonus cap, fixed → the flat grant amount.
              Hidden for AI_DYNAMIC (its range can't be localized — see the base fields above), and
              for a pure Free-Spins bonus (no cash-match component at all — matches FIELD_VISIBILITY's
              `amount`/`currencies` rules exactly, so this can't drift from what actually gets cleared). */}
          {!isAiDynamic && showPercentageAmount && (
            <div className="rounded-lg border border-[rgba(132,0,168,0.25)] p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className={labelClassName}>{t("bonus2.amountsByCurrency")}</span>
                <button
                  type="button"
                  onClick={addCurrencyRow}
                  disabled={!firstFreeCurrency}
                  className="px-2 py-1 bg-indigo-600 text-white text-xs rounded hover:bg-indigo-700 disabled:opacity-40 disabled:cursor-not-allowed"
                  title={firstFreeCurrency ? "" : t("bonus2.everyCurrencyHasRow")}
                >
                  {t("bonus2.addCurrency")}
                </button>
              </div>
              <p className={`${labelClassName} opacity-70 text-xs`}>{t("bonus2.usdBaseHint")}</p>
              <div className="space-y-3">
                {/* USD base row — locked and always present. Reads/writes the base amount fields,
                    which are DOLLARS in form state, so bridge to MoneyAmountInput's cents here. */}
                <div className={`grid grid-cols-1 ${amountRowGridColsClass} gap-2 items-start`}>
                  <div className="form-control">
                    <label className="label py-1">
                      <span className={labelClassName}>{t("bonus2.currency")}</span>
                    </label>
                    <select className={selectClassName} value="USD" disabled aria-label={t("bonus2.currency")}>
                      <option value="USD">USD</option>
                    </select>
                  </div>
                  {/* Ticket 2 — Min. Deposit doesn't apply to a pure No-Deposit bonus (no deposit
                      requirement). The pure Free-Spins case is handled one level up — this whole
                      table is hidden entirely for that type (see `showPercentageAmount` above),
                      so only No-Deposit needs a per-cell check here. Matches FIELD_VISIBILITY's
                      min_deposit rule exactly. The column itself collapses (amountRowGridColsClass)
                      rather than leaving an empty placeholder cell — removed completely, not hidden. */}
                  {!isNoDeposit && (
                    <MoneyAmountInput
                      label={t("bonus2.minDepositRequired")}
                      hideCurrency
                      currencies={[]}
                      value={{ amountCents: dollarsToCents(formData.min_deposit), currency: "USD" }}
                      onChange={(v) => handleChange("min_deposit", centsToDollars(v.amountCents))}
                    />
                  )}
                  {isPercentage ? (
                    <MoneyAmountInput
                      label={t("bonus2.maxBonus")}
                      hideCurrency
                      currencies={[]}
                      value={{ amountCents: dollarsToCents(formData.max_bonus_amount), currency: "USD" }}
                      onChange={(v) => handleChange("max_bonus_amount", centsToDollars(v.amountCents))}
                    />
                  ) : (
                    <MoneyAmountInput
                      label={t("bonus2.fixedAmountRequired")}
                      hideCurrency
                      required
                      currencies={[]}
                      value={{ amountCents: dollarsToCents(formData.amount), currency: "USD" }}
                      onChange={(v) => handleChange("amount", centsToDollars(v.amountCents))}
                    />
                  )}
                  {/* SOFT-736 — the USD row's stake IS the template's `free_spins_bet_value`: the
                      fallback every currency without a row of its own is converted from. Edited
                      here through the same picker as the others so the two never disagree about
                      units — it replaces the old free-text "Bet Value (cents)" field, which sat in
                      Free Spins Settings and asked for raw minor units next to inputs in dollars. */}
                  {showFreeSpins && (
                    <SpinStakeCell
                      coin="USD"
                      value={formData.free_spins_bet_value}
                      onChange={(minor) => handleChange("free_spins_bet_value", minor)}
                      betValues={betValues}
                      hasGame={(formData.free_spins_game_ids ?? []).length > 0}
                      issues={issuesForCurrency(saveIssues, "USD")}
                      label={t("bonus2.form.spinStakeFallback")}
                      isFallback
                    />
                  )}
                  <div />
                </div>

                {/* Additive NON-USD rows (stored in `currencies`, already in cents). */}
                {currencies.map((row, idx) => {
                  // Options for THIS row: every currency not taken by another row, plus this row's
                  // own value — so a currency can't be picked twice, but the row keeps its selection.
                  const rowCurrencyOptions = currencyOptions.filter((c) => c === row.currency || !usedCurrencies.has(c))
                  return (
                    <div key={row.currency || `row-${idx}`} className={`grid grid-cols-1 ${amountRowGridColsClass} gap-2 items-start`}>
                      <div className="form-control">
                        <label className="label py-1">
                          <span className={labelClassName}>{t("bonus2.currency")}</span>
                        </label>
                        <select
                          className={selectClassName}
                          value={row.currency}
                          onChange={(e) => setRowCurrency(idx, e.target.value)}
                          aria-label={t("bonus2.currency")}
                        >
                          {/* Keep an unknown/legacy currency selectable so an edited bonus never loses its value. */}
                          {row.currency && !currencyOptions.includes(row.currency) && <option value={row.currency}>{row.currency}</option>}
                          {rowCurrencyOptions.map((c) => (
                            <option key={c} value={c}>
                              {c}
                            </option>
                          ))}
                        </select>
                      </div>
                      {!isNoDeposit && (
                        <MoneyAmountInput
                          label={t("bonus2.minDeposit")}
                          hideCurrency
                          currencies={currencyOptions}
                          value={{ amountCents: row.min_deposit, currency: row.currency }}
                          onChange={(v) => setRowAmount(idx, "min_deposit", v.amountCents)}
                        />
                      )}
                      {isPercentage ? (
                        <MoneyAmountInput
                          label={t("bonus2.maxBonus")}
                          hideCurrency
                          currencies={currencyOptions}
                          value={{ amountCents: row.max_bonus_amount, currency: row.currency }}
                          onChange={(v) => setRowAmount(idx, "max_bonus_amount", v.amountCents)}
                        />
                      ) : (
                        <MoneyAmountInput
                          label={t("bonus2.fixedAmount")}
                          hideCurrency
                          currencies={currencyOptions}
                          value={{ amountCents: row.amount, currency: row.currency }}
                          onChange={(v) => setRowAmount(idx, "amount", v.amountCents)}
                        />
                      )}
                      {showFreeSpins && (
                        <SpinStakeCell
                          coin={row.currency}
                          value={row.free_spins_bet_value}
                          onChange={(minor) => setRowStake(idx, minor)}
                          betValues={betValues}
                          hasGame={(formData.free_spins_game_ids ?? []).length > 0}
                          issues={issuesForCurrency(saveIssues, row.currency)}
                        />
                      )}
                      <div className="form-control">
                        <label className="label py-1">
                          <span className={labelClassName}>&nbsp;</span>
                        </label>
                        <div className={controlLineClassName}>
                          <button type="button" onClick={() => removeCurrencyRow(idx)} className="btn btn-xs btn-error">
                            {t("bonus2.remove")}
                          </button>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            </div>
          )}
        </div>
      )

    // ------------------------------------------------------------------ 3
    case "wagering":
      return (
        <div className="space-y-4">
          <DescribeStrip>
            <Trans i18nKey="bonus2.form.wageringIntro" components={{ b: <strong /> }} />
          </DescribeStrip>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field
              label={t("bonus2.form.wageringMultiplier")}
              field="wagering_multiplier"
              warn={rangeWarning("wagering_multiplier", formData.wagering_multiplier)}
            >
              <input
                type="number"
                value={formData.wagering_multiplier}
                onChange={(e) => handleChange("wagering_multiplier", parseFloat(e.target.value))}
                className={inputClassName}
                step="0.1"
                required
              />
            </Field>
            {/* Tickets 1 & 5 — Requirement Based On + Which Bets Count are locked to Bonus
                amount / Only bonus-funded bets for No-Deposit and pure Free-Spins bonuses (the
                force-set effect in useBonusForm.ts keeps the value correct).
                No-Deposit: hidden entirely — a redundant, always-fixed input (this ticket).
                Pure Free-Spins: shown disabled, matching the Cap Based On / AI-Dynamic
                precedent for "visible but not editable" locked fields.
                Freespins+Deposit requires standard wagering-configuration rules (real dropdowns,
                not locked to bonus-only), same as any other deposit-triggered type. */}
            {!isNoDeposit && (
              <Field label={t("bonus2.form.requirementBasedOn")} field="wagering_base" desc={t("bonus2.form.requirementBasedOnDesc")}>
                {isFreeSpinsOnly ? (
                  <select value="BONUS_ONLY" disabled className={selectClassName}>
                    <option value="BONUS_ONLY">{t("bonus2.form.bonusAmountOption")}</option>
                  </select>
                ) : (
                  <select
                    value={formData.wagering_base}
                    onChange={(e) => handleChange("wagering_base", e.target.value)}
                    className={selectClassName}
                  >
                    {WAGERING_BASE_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.label)}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
            {!isNoDeposit && (
              <Field label={t("bonus2.form.whichBetsCount")} field="wagering_contribution" desc={t("bonus2.form.whichBetsCountDesc")} full>
                {isFreeSpinsOnly ? (
                  <select value="BONUS_BETS_ONLY" disabled className={selectClassName}>
                    <option value="BONUS_BETS_ONLY">{t("bonus2.form.onlyBonusFundedBets")}</option>
                  </select>
                ) : (
                  <select
                    value={formData.wagering_contribution}
                    onChange={(e) => handleChange("wagering_contribution", e.target.value)}
                    className={selectClassName}
                  >
                    {WAGERING_CONTRIBUTION_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {t(opt.label)}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
            )}
            {!isNoDeposit && (
              <div className="md:col-span-2">
                <DescribeStrip tone={contributionDescribe.tone}>{contributionDescribe.text}</DescribeStrip>
              </div>
            )}
            {/* R12 — wagering_accounts is intentionally NOT rendered: locked to BOTH_BALANCES. */}
            <Field label={t("bonus2.form.maxBetBonus")} field="max_bet_bonus" warn={rangeWarning("max_bet_bonus", formData.max_bet_bonus)}>
              <input
                type="number"
                value={formData.max_bet_bonus || ""}
                onChange={(e) => handleChange("max_bet_bonus", numOrClear(e.target.value))}
                className={inputClassName}
                step="0.01"
              />
            </Field>
            <Field
              label={t("bonus2.form.maxBetWagering")}
              field="max_bet_wagering"
              warn={rangeWarning("max_bet_wagering", formData.max_bet_wagering)}
            >
              <input
                type="number"
                value={formData.max_bet_wagering || ""}
                onChange={(e) => handleChange("max_bet_wagering", numOrClear(e.target.value))}
                className={inputClassName}
                step="0.01"
              />
            </Field>
            <div className="md:col-span-2">
              <DescribeStrip>{wageringPreview}</DescribeStrip>
            </div>
          </div>
        </div>
      )

    // ------------------------------------------------------------------ 4
    case "scope":
      return (
        <div className="space-y-4">
          {/* WHO may claim this bonus. Its OWN section rather than a panel inside "Eligibility
              & Games", because that one is hidden wholesale for Free-Spins bonuses (see
              BonusForm) — which would leave tag targeting unreachable for exactly those two
              types. Country/activity/new-customers below stay No-Deposit-only, unchanged. */}
          <Field label={t("bonus2.form.verification")} desc={t("bonus2.form.verificationDesc")}>
            <VerificationCheckboxes
              kyc={formData.kyc_required ?? false}
              emailVerified={formData.email_verified_required ?? false}
              phoneVerified={formData.phone_verified_required ?? false}
              showEmailPhone={isNoDepositType(formData.type)}
              onToggle={(role, checked) => {
                const field = {
                  kyc: "kyc_required",
                  email: "email_verified_required",
                  phone: "phone_verified_required"
                }[role] as keyof AdminBonus2
                handleChange(field, checked)
              }}
            />
            {/* "Only for new customers" has no dedicated field — it reuses
                offered_on_deposit_number=1 (zero deposits ever), covering both MNT and
                ProcessedDepositEvent deposit sources. No-Deposit only: the Activation
                section's Welcome Bonus control edits that same field, and two editors of one
                field on a deposit-triggered bonus would contradict each other. */}
            {isNoDeposit && (
              <TitledCheckbox
                id="new_customers_only"
                name="new_customers_only"
                title={t("bonus2.form.newCustomersOnly")}
                checked={formData.offered_on_deposit_number === 1}
                onChange={(e: ChangeEvent<HTMLInputElement>) => handleChange("offered_on_deposit_number", e.target.checked ? 1 : undefined)}
              />
            )}
          </Field>
          <Field label={t("bonus2.form.requiredTags")} desc={t("bonus2.form.requiredTagsDesc")}>
            <TagMultiSelect value={formData.allowed_segments ?? []} onChange={(slugs) => handleChange("allowed_segments", slugs)} />
            <p className={`${labelClassName} mt-2 opacity-70`}>
              {(formData.allowed_segments ?? []).length === 0
                ? (t("bonus2.form.segmentsNoRestriction") as string)
                : (t("bonus2.form.segmentsRestrictedTo", { count: (formData.allowed_segments ?? []).length }) as string)}
            </p>
          </Field>
          <Field label={t("bonus2.form.excludedTags")} desc={t("bonus2.form.excludedTagsDesc")}>
            <TagMultiSelect value={formData.excluded_segments ?? []} onChange={(slugs) => handleChange("excluded_segments", slugs)} />
          </Field>

          {isNoDeposit && (
            <>
              <Field label={t("bonus2.form.countryEligibility")} desc={t("bonus2.form.countryEligibilityDesc")}>
                <TagsMultiSelect
                  options={countries}
                  value={formData.allowed_countries ?? []}
                  onChange={(vals) => handleChange("allowed_countries", vals)}
                  placeholder={t("bonus2.form.allCountriesEligible")}
                  panelLabel={t("bonus2.form.countries") as string}
                  rounded={false}
                />
              </Field>
              <Field label={t("bonus2.form.excludedCountries")} desc={t("bonus2.form.excludedCountriesDesc")}>
                <TagsMultiSelect
                  options={countries}
                  value={formData.excluded_countries ?? []}
                  onChange={(vals) => handleChange("excluded_countries", vals)}
                  placeholder={t("bonus2.form.noCountriesExcluded")}
                  panelLabel={t("bonus2.form.countries") as string}
                  rounded={false}
                />
              </Field>
              <Field label={t("bonus2.form.activityRequirement")} desc={t("bonus2.form.activityRequirementDesc")}>
                <select
                  value={formData.activity_requirement_type ?? ""}
                  onChange={(e) =>
                    handleChange("activity_requirement_type", (e.target.value || null) as AdminBonus2["activity_requirement_type"])
                  }
                  className={selectClassName}
                >
                  <option value="">{t("bonus2.form.noRestriction")}</option>
                  <option value="NO_BONUS_CLAIMED_IN_PERIOD">{t("bonus2.form.noBonusClaimedInPeriod")}</option>
                </select>
                {formData.activity_requirement_type === "NO_BONUS_CLAIMED_IN_PERIOD" && (
                  <input
                    type="number"
                    min={1}
                    value={formData.activity_period_days ?? ""}
                    onChange={(e) => handleChange("activity_period_days", e.target.value ? Number(e.target.value) : undefined)}
                    placeholder={t("bonus2.form.periodDays")}
                    className={`${inputClassName} mt-2`}
                  />
                )}
              </Field>
            </>
          )}

          {/* A PURE Free Spins bonus picks its games in the Amount section
              (free_spins_game_ids) and nowhere else, so these blocks would be a second,
              conflicting answer for it. Deposit+Spins keeps them: its cash half plays on
              ordinary games and needs the same eligibility as any deposit bonus. WHO may claim
              it, above, applies to every type. */}
          {showEligibilityScope && (
            <>
              {/* Eligibility (products) FIRST — the high-level "where can this bonus be used". */}
              <Field label={t("bonus2.form.eligibleProducts")} desc={t("bonus2.form.eligibleProductsDesc")}>
                <div className="flex flex-wrap gap-4">
                  {ALLOWED_PRODUCT_OPTIONS.map(({ value, label }) => {
                    // "All enabled by default": an empty allowed_products means ALL products
                    // (backend semantics), so show every box checked until the admin unchecks one.
                    const list = formData.allowed_products
                    const checked = !list || list.length === 0 || list.includes(value)
                    return (
                      <label key={value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={checked}
                          onChange={() => {
                            // First uncheck from the "all" state → materialize the full list minus this one.
                            const current = !list || list.length === 0 ? ALLOWED_PRODUCT_OPTIONS.map((o) => o.value) : list
                            const next = current.includes(value) ? current.filter((v) => v !== value) : [...current, value]
                            // If everything is selected again, store empty (= "all", the neutral default).
                            handleChange("allowed_products", next.length === ALLOWED_PRODUCT_OPTIONS.length ? [] : next)
                          }}
                        />
                        <span className={labelClassName}>{t(label)}</span>
                      </label>
                    )
                  })}
                </div>
              </Field>

              {/* Empty = every currency; one without an amount row converts from the USD base by FX. */}
              <Field label={t("bonus2.eligibleCurrencies")} desc={t("bonus2.eligibleCurrenciesDesc")}>
                <MultiSelect
                  options={eligibilityCurrencies.map((c) => ({ value: c, label: c }))}
                  value={formData.allowed_currencies ?? []}
                  onChange={(next) => handleChange("allowed_currencies", next)}
                  allLabel={t("bonus2.eligibleAllCurrencies") as string}
                />
              </Field>

              {/* Iteration-3 Fix 14 — Eligible Payments (fiat/crypto). Both eligible by default
                (empty list). Checking one restricts deposit activation to that class. */}
              <Field label={t("bonus2.form.eligiblePayments")} desc={t("bonus2.form.eligiblePaymentsDesc")}>
                <div className="flex flex-wrap gap-4">
                  {ELIGIBLE_PAYMENT_OPTIONS.map((opt) => {
                    // "Both enabled by default": an empty eligible_payments means BOTH classes
                    // (backend semantics — isPaymentClassEligible: empty = allow all). Show every
                    // box checked until the admin unchecks one, mirroring the Eligible Products list.
                    const list = formData.eligible_payments ?? []
                    const checked = list.length === 0 || list.includes(opt.value)
                    return (
                      <label key={opt.value} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={checked}
                          onChange={() => {
                            // First uncheck from the "all" (empty) state → materialize the full
                            // list minus this one, so the remaining class stays selected.
                            const current = list.length === 0 ? ELIGIBLE_PAYMENT_OPTIONS.map((o) => o.value) : list
                            const next = current.includes(opt.value) ? current.filter((c) => c !== opt.value) : [...current, opt.value]
                            // If everything is selected again, store [] (= both, the neutral default).
                            handleChange("eligible_payments", next.length === ELIGIBLE_PAYMENT_OPTIONS.length ? [] : next)
                          }}
                        />
                        <span className={labelClassName}>{t(opt.label)}</span>
                      </label>
                    )
                  })}
                </div>
                <p className={`${labelClassName} mt-2 opacity-70`}>
                  {(formData.eligible_payments ?? []).length === 0
                    ? (t("bonus2.form.paymentsNoRestriction") as string)
                    : (t("bonus2.form.paymentsRestrictedTo", { list: (formData.eligible_payments ?? []).join(", ") }) as string)}
                </p>
              </Field>

              {/* Game-level allow/restrict BELOW the product eligibility. */}
              <CollapsibleGamePicker
                label={t("bonus2.form.allowedGames")}
                selectedGameIds={formData.allowed_game_ids || []}
                onGameIdsChange={(gameIds) => handleChange("allowed_game_ids", gameIds)}
                tenant={tenant}
              />
              {/* Naming one game silently excludes the whole rest of the catalogue, which is how
                  a live bonus ended up unusable on the game its own free spins ran on. */}
              {(formData.allowed_game_ids ?? []).length > 0 && (
                <DescribeStrip tone="warn">
                  {t("bonus2.form.allowedGamesBlastRadius", { count: (formData.allowed_game_ids ?? []).length })}
                </DescribeStrip>
              )}
              <CollapsibleGamePicker
                label={t("bonus2.form.restrictedGames")}
                selectedGameIds={formData.restricted_game_ids || []}
                onGameIdsChange={(gameIds) => handleChange("restricted_game_ids", gameIds)}
                tenant={tenant}
              />
              <DescribeStrip>{t("bonus2.form.gameEligibilityIntro")}</DescribeStrip>

              <Field label={t("bonus2.form.allowedGameGroups")}>
                {gameGroups.length === 0 ? (
                  <span className="label-text-alt text-gray-400">{t("bonus2.form.noGameGroups")}</span>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {gameGroups.map((group) => (
                      <label key={group.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={(formData.allowed_game_group_ids || []).includes(group.id)}
                          onChange={() => toggleGameGroup("allowed_game_group_ids", group.id)}
                        />
                        <span className={labelClassName}>
                          {group.name} ({group.game_ids.length})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </Field>
              <Field label={t("bonus2.form.restrictedGameGroups")}>
                {gameGroups.length === 0 ? (
                  <span className="label-text-alt text-gray-400">{t("bonus2.form.noGameGroups")}</span>
                ) : (
                  <div className="flex flex-wrap gap-4">
                    {gameGroups.map((group) => (
                      <label key={group.id} className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          className="checkbox checkbox-primary checkbox-sm"
                          checked={(formData.restricted_game_group_ids || []).includes(group.id)}
                          onChange={() => toggleGameGroup("restricted_game_group_ids", group.id)}
                        />
                        <span className={labelClassName}>
                          {group.name} ({group.game_ids.length})
                        </span>
                      </label>
                    ))}
                  </div>
                )}
              </Field>
            </>
          )}
        </div>
      )

    case "violations":
      return (
        <div className="space-y-4">
          <Field label={t("bonus2.form.maxBetExceeded")} field="max_bet_actions" desc={t("bonus2.form.maxBetExceededDesc")}>
            <div className="flex flex-wrap gap-4">
              {[
                { code: "CANCEL_BONUS", label: "bonus2.opt.action.cancelBonus" },
                { code: "ADD_LOG", label: "bonus2.opt.action.addLog" },
                { code: "EXCLUDE_WAGERING", label: "bonus2.opt.action.excludeWagering" }
              ].map(({ code, label }) => (
                <label key={code} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary checkbox-sm"
                    checked={(formData.max_bet_actions || []).includes(code)}
                    onChange={() => toggleAction("max_bet_actions", code)}
                  />
                  <span className={labelClassName}>{t(label)}</span>
                  <Tooltip text={FIELD_TOOLTIPS[`action_${code}`]} />
                </label>
              ))}
            </div>
          </Field>
          <Field
            label={t("bonus2.form.restrictedGamePlayed")}
            field="restricted_game_actions"
            desc={t("bonus2.form.restrictedGamePlayedDesc")}
          >
            <div className="flex flex-wrap gap-4">
              {[
                { code: "PREVENT_LAUNCH", label: "bonus2.opt.action.preventLaunch" },
                { code: "CANCEL_BONUS", label: "bonus2.opt.action.cancelBonus" },
                { code: "ADD_LOG", label: "bonus2.opt.action.addLog" },
                { code: "EXCLUDE_WAGERING", label: "bonus2.opt.action.excludeWagering" }
              ].map(({ code, label }) => (
                <label key={code} className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    className="checkbox checkbox-primary checkbox-sm"
                    checked={(formData.restricted_game_actions || []).includes(code)}
                    onChange={() => toggleAction("restricted_game_actions", code)}
                  />
                  <span className={labelClassName}>{t(label)}</span>
                  <Tooltip text={FIELD_TOOLTIPS[`action_${code}`]} />
                </label>
              ))}
            </div>
          </Field>
          {preventLaunchSelected && (
            <DescribeStrip>{t("bonus2.form.preventLaunchDesc")}</DescribeStrip>
          )}
        </div>
      )

    // ------------------------------------------------------------------ 5
    case "activation":
      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Field label={t("bonus2.form.activationEvent")}>
            <select
              value={formData.activation_event}
              onChange={(e) => handleChange("activation_event", e.target.value)}
              className={selectClassName}
            >
              {/* Fix 10 — hide First/Reload deposit; show Wager Threshold / VIP / Birthday.
                  Ticket 11 — "On Deposit" is not a valid trigger for No-Deposit bonuses.
                  "No Trigger (User)" is only offered for Free Spins / Deposit+Spins / No-Deposit
                  — the types this player-claim mechanism actually exists for. */}
              {SELECTABLE_ACTIVATION_EVENT_OPTIONS.filter(
                (opt) => !(isNoDeposit && opt.value === "DEPOSIT") && !(opt.value === "NO_TRIGGER_USER" && !isNoDeposit && !showFreeSpins)
              ).map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </option>
              ))}
            </select>
          </Field>

          {showPromoCode && (
            <Field label={t("bonus2.form.promoCodes")} field="promo_codes" full>
              <PromoCodeListEditor
                codes={formData.promo_codes ?? []}
                onChange={(codes) => handleChange("promo_codes", codes)}
                tenant={tenant}
                refused={refusedCodes}
                savedCodes={savedPromoCodes}
                bonusName={formData.name}
                disabled={Boolean(formData.archived)}
                disabledReason={t("bonus2.promo.archivedNoCodes") as string}
              />
            </Field>
          )}

          {!showPromoCode && (formData.promo_codes?.length ?? 0) > 0 && (
            <Field label={t("bonus2.form.promoCodes")}>
              <p className="text-sm text-gray-400">{t("bonus2.promo.managedInTab", { count: formData.promo_codes?.length ?? 0 })}</p>
            </Field>
          )}

          {/* Iteration-3 Fix 10 — Wager Threshold amount (shown only for BET_THRESHOLD, which the
              UI labels "On Wager Threshold"). Subscribe the player once cumulative wager crosses it. */}
          {formData.activation_event === "BET_THRESHOLD" && (
            <Field label={t("bonus2.form.wagerAmount")} desc={t("bonus2.form.wagerAmountDesc")}>
              <input
                type="number"
                value={formData.wager_amount ?? ""}
                onChange={(e) => handleChange("wager_amount", e.target.value ? parseInt(e.target.value) : undefined)}
                className={inputClassName}
              />
            </Field>
          )}

          {/* Fix 10 — VIP tier multi-select (shown only for VIP_LEVEL_UP). Comma-separated levels. */}
          {formData.activation_event === "VIP_LEVEL_UP" && (
            <Field label={t("bonus2.form.vipLevels")} desc={t("bonus2.form.vipLevelsDesc")}>
              <input
                type="text"
                placeholder={t("bonus2.form.vipLevelsPlaceholder")}
                value={(formData.vip_tiers ?? []).join(", ")}
                onChange={(e) =>
                  handleChange(
                    "vip_tiers",
                    e.target.value
                      .split(",")
                      .map((s) => parseInt(s.trim()))
                      .filter((n) => !Number.isNaN(n))
                  )
                }
                className={inputClassName}
              />
            </Field>
          )}

          <Field label={t("bonus2.form.welcomeBonus")} desc={t("bonus2.form.welcomeBonusDesc")}>
            <div className="flex items-center gap-3">
              <input
                type="checkbox"
                id="welcome_bonus"
                checked={isWelcomeBonus}
                onChange={(e) => handleWelcomeBonusToggle(e.target.checked)}
                className="checkbox checkbox-primary"
              />
              <label htmlFor="welcome_bonus" className="dark:text-white cursor-pointer">
                Offer only on a specific deposit
              </label>
            </div>
            {isWelcomeBonus && (
              <select
                value={formData.offered_on_deposit_number ?? 1}
                onChange={(e) => handleChange("offered_on_deposit_number", parseInt(e.target.value))}
                className={`${selectClassName} mt-2`}
              >
                <option value="1">{t("bonus2.form.deposit1")}</option>
                <option value="2">{t("bonus2.form.deposit2")}</option>
                <option value="3">{t("bonus2.form.deposit3")}</option>
                <option value="4">{t("bonus2.form.deposit4")}</option>
                <option value="5">{t("bonus2.form.deposit5")}</option>
              </select>
            )}
          </Field>

          <Field
            label={t("bonus2.form.expirationDays")}
            field="expiration_days"
            warn={rangeWarning("expiration_days", formData.expiration_days)}
          >
            <input
              type="number"
              value={formData.expiration_days}
              onChange={(e) => handleChange("expiration_days", parseInt(e.target.value))}
              className={inputClassName}
              required
            />
          </Field>
          <Field label={t("bonus2.form.priority")} field="priority">
            <input
              type="number"
              value={formData.priority}
              onChange={(e) => handleChange("priority", parseInt(e.target.value))}
              className={inputClassName}
              required
            />
          </Field>

          <Field label={t("bonus2.form.campaignStart")}>
            <input
              type="datetime-local"
              value={formData.starts_at_iso ? formData.starts_at_iso.slice(0, 16) : ""}
              onChange={(e) => handleChange("starts_at_iso", e.target.value ? new Date(e.target.value).toISOString() : undefined)}
              className={inputClassName}
            />
          </Field>
          <Field label={t("bonus2.form.campaignEnd")}>
            <input
              type="datetime-local"
              value={formData.ends_at_iso ? formData.ends_at_iso.slice(0, 16) : ""}
              onChange={(e) => handleChange("ends_at_iso", e.target.value ? new Date(e.target.value).toISOString() : undefined)}
              className={inputClassName}
            />
          </Field>

          {/* Iteration-3 Fix 12 — recurring availability window (days / hours / timezone).
              Persisted to bonus-service `time_restrictions` and enforced at eligibility. */}
          <Field label={t("bonus2.form.recurringAvailability")} desc={t("bonus2.form.recurringAvailabilityDesc")}>
            <div className="space-y-3">
              <div>
                <div className="text-xs text-gray-500 mb-1">{t("bonus2.form.daysOfWeek")}</div>
                <div className="flex flex-wrap gap-2">
                  {WEEKDAY_OPTIONS.map((d) => {
                    const days = formData.time_restrictions?.days ?? []
                    const on = days.includes(d.value)
                    return (
                      <button
                        type="button"
                        key={d.value}
                        onClick={() => handleTimeRestrictionToggle("days", d.value)}
                        className={`px-2 py-1 text-xs rounded border ${
                          on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        {t(d.label)}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">{t("bonus2.form.hoursOfDay")}</div>
                <div className="flex flex-wrap gap-1">
                  {Array.from({ length: 24 }, (_, h) => {
                    const hours = formData.time_restrictions?.hours ?? []
                    const on = hours.includes(h)
                    return (
                      <button
                        type="button"
                        key={h}
                        onClick={() => handleTimeRestrictionToggle("hours", h)}
                        className={`w-8 py-1 text-xs rounded border ${
                          on ? "bg-indigo-600 text-white border-indigo-600" : "bg-white text-gray-700 border-gray-300"
                        }`}
                      >
                        {h}
                      </button>
                    )
                  })}
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-500 mb-1">{t("bonus2.form.timezone")}</div>
                <TimezoneSelect value={formData.time_restrictions?.timezone} onChange={handleTimeRestrictionTimezone} />
                {timezoneWithoutWindow && (
                  <span className="mt-1 block text-xs text-amber-300">
                    ⚠ Select a day or an hour above — a timezone on its own is not saved.
                  </span>
                )}
              </div>
            </div>
          </Field>

          <Field label={t("bonus2.form.stackingMode")}>
            <select
              value={formData.stacking_mode}
              onChange={(e) => handleStackingChange(e.target.value as AdminBonus2["stacking_mode"])}
              className={selectClassName}
            >
              {STACKING_MODE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {t(opt.label)}
                </option>
              ))}
            </select>
          </Field>
        </div>
      )

    // ------------------------------------------------------------------ 6
    case "content":
      return (
        <div className="space-y-4">
          <Field label={t("bonus2.form.bonusBanner")}>
            <div className="flex gap-4 mb-2">
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="banner_mode"
                  className="radio radio-primary radio-sm"
                  checked={bannerMode === "select"}
                  onChange={() => setBannerMode("select")}
                />
                <span className={labelClassName}>{t("bonus2.form.selectExisting")}</span>
              </label>
              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="radio"
                  name="banner_mode"
                  className="radio radio-primary radio-sm"
                  checked={bannerMode === "create"}
                  onChange={() => setBannerMode("create")}
                />
                <span className={labelClassName}>{t("bonus2.form.uploadNew")}</span>
              </label>
            </div>

            {bannerMode === "select" ? (
              <>
                <select
                  value={cmsBanners.find((b) => b.banner_image === formData.banner_url)?.id || ""}
                  onChange={(e) => selectCmsBanner(e.target.value)}
                  className={selectClassName}
                >
                  <option value="">{t("bonus2.form.noBanner")}</option>
                  {cmsBanners.map((b) => (
                    <option key={b.id} value={b.id}>
                      {b.name}
                      {b.active ? "" : " (inactive)"}
                    </option>
                  ))}
                </select>
                {cmsBanners.length === 0 && (
                  <label className="label">
                    <span className="label-text-alt text-gray-400">
                      No CMS banners yet — create one here or on CMS Pages &quot;Bonus Banner&quot; tab.
                    </span>
                  </label>
                )}
              </>
            ) : (
              <div className="space-y-2 pl-4 border-l-2 border-[rgba(132,0,168,0.5)]">
                <input
                  type="text"
                  placeholder={t("bonus2.form.bannerName")}
                  value={newBannerName}
                  onChange={(e) => setNewBannerName(e.target.value)}
                  className={inputClassName}
                />
                <FileUpload
                  id="new_banner_desktop"
                  title={t("bonus2.form.uploadDesktop")}
                  setFieldValue={(_id: string, url: string) => setNewBannerDesktopUrl(url)}
                />
                <FileUpload
                  id="new_banner_mobile"
                  title={t("bonus2.form.uploadMobile")}
                  setFieldValue={(_id: string, url: string) => setNewBannerMobileUrl(url)}
                />
                {(newBannerDesktopUrl || newBannerMobileUrl) && (
                  <div className="flex flex-wrap gap-4">
                    {newBannerDesktopUrl && (
                      <div>
                        <div className="text-xs text-gray-400">{t("bonus2.form.desktop")}</div>
                        <img
                          src={newBannerDesktopUrl}
                          alt={t("bonus2.form.newBannerDesktopAlt") as string}
                          className="mt-1 max-h-32 w-auto rounded border border-base-300 object-contain"
                        />
                      </div>
                    )}
                    {newBannerMobileUrl && (
                      <div>
                        <div className="text-xs text-gray-400">{t("bonus2.form.mobile")}</div>
                        <img
                          src={newBannerMobileUrl}
                          alt={t("bonus2.form.newBannerMobileAlt") as string}
                          className="mt-1 max-h-32 w-auto rounded border border-base-300 object-contain"
                        />
                      </div>
                    )}
                  </div>
                )}
                <button type="button" onClick={handleCreateBanner} disabled={savingBanner} className="btn btn-primary btn-sm">
                  {savingBanner ? t("bonus2.form.saving") : t("bonus2.form.createAttachBanner")}
                </button>
              </div>
            )}

            {(selectedBanner?.desktop_url || selectedBanner?.mobile_url) && (
              <div className="mt-3">
                <span className="label-text-alt text-gray-400">{t("bonus2.form.selectedBannerPreview")}</span>
                <div className="mt-1 flex flex-wrap gap-4">
                  {selectedBanner?.desktop_url && (
                    <div>
                      <div className="text-xs text-gray-400">{t("bonus2.form.desktop")}</div>
                      <img
                        src={selectedBanner.desktop_url}
                        alt={`Banner ${selectedBanner.name} (desktop)`}
                        className="mt-1 max-h-32 w-auto rounded border border-base-300 object-contain"
                      />
                    </div>
                  )}
                  {selectedBanner?.mobile_url && (
                    <div>
                      <div className="text-xs text-gray-400">{t("bonus2.form.mobile")}</div>
                      <img
                        src={selectedBanner.mobile_url}
                        alt={`Banner ${selectedBanner.name} (mobile)`}
                        className="mt-1 max-h-32 w-auto rounded border border-base-300 object-contain"
                      />
                    </div>
                  )}
                </div>
              </div>
            )}
          </Field>
        </div>
      )

    // ------------------------------------------------------------------ 7
    case "advanced":
      return (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label={t("bonus2.form.maxActivations")} field="max_activations_per_player">
              <input
                type="number"
                value={formData.max_activations_per_player || ""}
                onChange={(e) => handleChange("max_activations_per_player", intOrClear(e.target.value))}
                className={inputClassName}
              />
            </Field>
            <Field label={t("bonus2.form.cooldownDays")} field="cooldown_days">
              <input
                type="number"
                value={formData.cooldown_days ?? ""}
                onChange={(e) => handleChange("cooldown_days", intOrClear(e.target.value))}
                className={inputClassName}
              />
            </Field>
            <Field label={t("bonus2.form.maxTotalClaims")} field="max_total_claims" desc={t("bonus2.form.maxTotalClaimsDesc")}>
              <input
                type="number"
                value={formData.max_total_claims || ""}
                onChange={(e) => handleChange("max_total_claims", intOrClear(e.target.value))}
                className={inputClassName}
              />
            </Field>
          </div>
        </div>
      )

    default:
      return null
  }
}
